/**
 * California Base Building Codes
 *
 * Defines the California Building Standards Code (Title 24) sections
 * that are shared across all California jurisdictions.
 *
 * Individual jurisdictions inherit from this base and can:
 * - Override sections with local amendments
 * - Add jurisdiction-specific sections
 * - Specify their adoption dates
 */

/**
 * California code family definitions
 * These are the standard Title 24 parts that all CA jurisdictions adopt
 */
export const CALIFORNIA_CODES = {
  residential: {
    name: 'California Residential Code',
    abbrev: 'CRC',
    title24Part: '2.5',
  },
  building: {
    name: 'California Building Code',
    abbrev: 'CBC',
    title24Part: '2',
  },
  electrical: {
    name: 'California Electrical Code',
    abbrev: 'CEC',
    title24Part: '3',
  },
  mechanical: {
    name: 'California Mechanical Code',
    abbrev: 'CMC',
    title24Part: '4',
  },
  plumbing: {
    name: 'California Plumbing Code',
    abbrev: 'CPC',
    title24Part: '5',
  },
  energy: {
    name: 'California Energy Code',
    abbrev: 'Title 24, Part 6',
    title24Part: '6',
  },
  fire: {
    name: 'California Fire Code',
    abbrev: 'CFC',
    title24Part: '9',
  },
  green: {
    name: 'California Green Building Standards Code',
    abbrev: 'CALGreen',
    title24Part: '11',
  },
};

/**
 * Current California code cycle
 * Update this when California adopts new code cycles (every 3 years)
 */
export const CURRENT_CA_CODE_YEAR = '2022';
export const NEXT_CA_CODE_YEAR = '2025'; // Effective January 2026

/**
 * Base sections that apply to all California jurisdictions
 * Jurisdictions can override or extend these
 *
 * Section structure:
 * - id: unique identifier for the section
 * - title: display title
 * - scope: array of project scopes this applies to (empty = always include)
 * - items: array of strings or objects with text + subItems
 *
 * Placeholders:
 * - {codeYear}: replaced with jurisdiction's adopted code year
 * - {localCode}: replaced with jurisdiction's local code reference
 */
export const BASE_SECTIONS = [
  {
    id: 'energy-compliance',
    title: 'ENERGY COMPLIANCE (TITLE 24, PART 6)',
    scope: [], // Always include for remodels
    items: [
      'All work affecting the building envelope, HVAC, water heating, or lighting shall comply with the {codeYear} California Energy Code.',
      'CF-1R Registration and Certificate of Compliance shall be posted at the job site and available for inspection.',
      'HERS field verification and diagnostic testing required where indicated on CF-1R documents.',
      'CF-6R Installation Certificates shall be completed by the installing contractor for applicable measures.',
    ],
  },
  {
    id: 'electrical',
    title: 'ELECTRICAL',
    scope: ['electrical', 'kitchen', 'bathroom', 'addition', 'full-remodel'],
    items: [
      'All electrical work shall comply with the {codeYear} California Electrical Code{localCodeRef}.',
      'Arc-Fault Circuit Interrupter (AFCI) protection required for all 15A and 20A, 120V branch circuits supplying outlets and devices in kitchens, family rooms, dining rooms, living rooms, parlors, libraries, dens, bedrooms, sunrooms, recreation rooms, closets, hallways, laundry areas, and similar rooms or areas.',
      'Ground-Fault Circuit Interrupter (GFCI) protection required for all receptacles in bathrooms, garages, outdoors, crawl spaces, basements, kitchens (serving countertops), laundry areas, and within 6 feet of sinks.',
      'Tamper-resistant receptacles required for all 15A and 20A, 125V receptacles in dwelling units.',
      'All electrical panels shall be accessible with minimum 30" wide x 36" deep clear working space and minimum 78" headroom.',
    ],
  },
  {
    id: 'plumbing',
    title: 'PLUMBING',
    scope: ['plumbing', 'kitchen', 'bathroom', 'addition', 'full-remodel'],
    items: [
      'All plumbing work shall comply with the {codeYear} California Plumbing Code{localCodeRef}.',
      "Water heaters shall be strapped with approved seismic straps at upper and lower third of unit per manufacturer's instructions and CPC requirements.",
      'Water heater T&P relief valve shall discharge to an approved location, not more than 6" above grade or approved receptor.',
      {
        text: 'Maximum flow rates for fixtures (California requirements):',
        subItems: [
          'Lavatory faucets: 1.2 GPM',
          'Kitchen faucets: 1.8 GPM',
          'Showerheads: 1.8 GPM',
          'Toilets: 1.28 GPF',
        ],
      },
      'Expansion tank required where water heater is served by a pressure regulator, backflow preventer, or check valve.',
    ],
  },
  {
    id: 'mechanical',
    title: 'MECHANICAL / HVAC',
    scope: [
      'mechanical',
      'hvac',
      'kitchen',
      'bathroom',
      'addition',
      'full-remodel',
    ],
    items: [
      'All mechanical work shall comply with the {codeYear} California Mechanical Code{localCodeRef}.',
      'Bathrooms shall be provided with mechanical exhaust ventilation capable of providing 50 CFM intermittent or 20 CFM continuous.',
      'Kitchen exhaust required: Range hoods shall exhaust to the exterior and provide minimum 100 CFM.',
      "HVAC equipment shall be installed per manufacturer's specifications and Title 24 requirements.",
      'All duct penetrations through fire-rated assemblies shall be protected with approved fire dampers.',
      'Refrigerant piping shall be installed in accordance with CMC Chapter 11.',
    ],
  },
  {
    id: 'structural',
    title: 'STRUCTURAL / SEISMIC',
    scope: ['structural', 'addition', 'full-remodel'],
    items: [
      'Seismic Design Category: D (per ASCE 7-22 and CBC Chapter 16)',
      'All structural work shall be performed in accordance with the approved plans. Any deviations shall be approved by the Engineer of Record prior to construction.',
      'Existing structural elements to remain shall be protected during construction. Any damage shall be repaired to match existing or better.',
      'Wall bracing shall comply with CRC Section R602.10 or engineered design.',
      "Hold-downs and anchor bolts shall be installed per approved plans and manufacturer's specifications.",
    ],
  },
  {
    id: 'fire-safety',
    title: 'FIRE & LIFE SAFETY',
    scope: [], // Always include
    items: [
      'Smoke alarms required in each sleeping room, outside each sleeping area, and on each story including basements. All smoke alarms shall be interconnected and hardwired with battery backup per CRC R314.',
      'Carbon monoxide (CO) alarms required outside each sleeping area and on each story with fuel-burning appliances or attached garage. CO alarms shall be hardwired with battery backup per CRC R315.',
      'Fire blocking required at all concealed draft openings per CRC R602.8.',
      'Tempered safety glazing required where glazing is within 24" of doors, in wet areas, and where bottom edge is less than 18" above floor per CBC Chapter 24.',
    ],
  },
  {
    id: 'demolition',
    title: 'DEMOLITION & HAZARDOUS MATERIALS',
    scope: ['demolition', 'full-remodel'],
    items: [
      'Contractor shall verify presence of asbestos-containing materials (ACM) and lead-based paint prior to demolition. Testing by certified professionals required for structures built before 1978.',
      'Abatement of hazardous materials shall be performed by licensed and certified professionals in accordance with SCAQMD Rule 1403 and Cal/OSHA requirements.',
      'All demolition debris shall be disposed of at approved facilities in accordance with local regulations.',
      'Dust control measures shall be implemented during demolition per SCAQMD Rule 403.',
    ],
  },
  {
    id: 'general-construction',
    title: 'GENERAL CONSTRUCTION',
    scope: [], // Always include
    items: [
      'All work shall be performed in a workmanlike manner by properly licensed contractors.',
      'Minimum ceiling height: 7\'-0" for habitable rooms, 6\'-8" for bathrooms and hallways per CRC R305.',
      'Emergency egress required from all sleeping rooms: Minimum 5.7 SF opening, minimum 24" height, minimum 20" width, maximum 44" sill height per CRC R310.',
      'Stairway requirements: Minimum 36" width, maximum 7-3/4" riser height, minimum 10" tread depth per CRC R311.7.',
      'Handrails required on stairs with 4 or more risers. Graspable handrail required on at least one side per CRC R311.7.8.',
    ],
  },
  {
    id: 'accessibility',
    title: 'ACCESSIBILITY (WHERE APPLICABLE)',
    scope: ['accessibility', 'addition'],
    items: [
      'Accessibility requirements per CBC Chapter 11A and 11B where applicable.',
      'New additions shall comply with current accessibility standards.',
      'Alterations affecting the path of travel may trigger accessibility upgrades up to 20% of the project valuation.',
    ],
  },
];

export default {
  CALIFORNIA_CODES,
  CURRENT_CA_CODE_YEAR,
  NEXT_CA_CODE_YEAR,
  BASE_SECTIONS,
};
